import { useCallback, useEffect, useMemo, useState } from 'react';

// localStorage key. Value: '1' (user wants desktop notifications) / '0' (opted out).
// Distinct from browser permission — the user can have permission granted but
// still choose to mute desktop notifications from our in-app toggle.
const PREF_KEY = 'intver2.desktopNotifications.enabled';

type Permission = 'default' | 'granted' | 'denied' | 'unsupported';

function readPermission(): Permission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

function readEnabledPref(): boolean {
  if (typeof window === 'undefined') return false;
  const raw = window.localStorage.getItem(PREF_KEY);
  // Default: ON once permission is granted. Explicit '0' opts out.
  return raw !== '0';
}

/**
 * Phase A of web push — show OS-level notifications while the CRM tab is open.
 *
 * - Uses the Notifications Web API directly (no service worker, no VAPID).
 * - `fire()` is a no-op unless permission === 'granted' and the user hasn't
 *   explicitly disabled it via the settings toggle.
 * - By default we only fire when the tab is not focused, so foreground use
 *   doesn't double up with the in-app bell.
 */
export function useDesktopNotification() {
  const [permission, setPermission] = useState<Permission>(readPermission);
  const [enabled, setEnabled] = useState<boolean>(readEnabledPref);

  // Keep permission state in sync if the user changes it in browser settings.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('permissions' in navigator)) return;
    let status: PermissionStatus | undefined;
    let cancelled = false;

    (async () => {
      try {
        status = await navigator.permissions.query({ name: 'notifications' as PermissionName });
        if (cancelled) return;
        const handler = () => setPermission(readPermission());
        status.addEventListener('change', handler);
        // Store cleanup via closure below.
        (status as any)._ourHandler = handler;
      } catch {
        // Not all browsers support permissions.query for notifications (Safari < 16).
      }
    })();

    return () => {
      cancelled = true;
      if (status && (status as any)._ourHandler) {
        status.removeEventListener('change', (status as any)._ourHandler);
      }
    };
  }, []);

  const request = useCallback(async (): Promise<Permission> => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === 'granted') {
      window.localStorage.setItem(PREF_KEY, '1');
      setEnabled(true);
    }
    return result;
  }, []);

  const setEnabledPersist = useCallback((value: boolean) => {
    setEnabled(value);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(PREF_KEY, value ? '1' : '0');
    }
  }, []);

  const fire = useCallback(
    (opts: {
      title: string;
      body?: string;
      tag?: string;
      data?: unknown;
      onClick?: () => void;
      /** Only fire when the tab is hidden/blurred. Defaults to true. */
      onlyWhenHidden?: boolean;
    }) => {
      if (typeof window === 'undefined' || !('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;
      if (!readEnabledPref()) return;
      // Web push (service worker) subscribed → the SW shows this notification.
      if (window.localStorage.getItem('intver2.webPush.subscribed') === '1') return;

      const onlyHidden = opts.onlyWhenHidden !== false;
      if (onlyHidden) {
        const isHidden = document.visibilityState === 'hidden' || !document.hasFocus();
        if (!isHidden) return;
      }

      try {
        const n = new Notification(opts.title, {
          body: opts.body,
          tag: opts.tag,
          data: opts.data,
          icon: '/icon-192.png',
          silent: false,
        });
        n.onclick = () => {
          window.focus();
          opts.onClick?.();
          n.close();
        };
      } catch (err) {
        console.warn('[desktop-notification] failed to fire:', err);
      }
    },
    [],
  );

  return useMemo(
    () => ({
      /** 'granted' | 'denied' | 'default' | 'unsupported' */
      permission,
      /** User's in-app opt-in (separate from browser permission). */
      enabled,
      /** Prompts for browser permission. */
      request,
      /** Mute / unmute in-app without changing browser permission. */
      setEnabled: setEnabledPersist,
      /** Fire an OS notification (no-op unless permission + enabled + tab hidden). */
      fire,
    }),
    [permission, enabled, request, setEnabledPersist, fire],
  );
}
